import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import { usePage } from '@inertiajs/react';
import classNames from 'classnames';
import { isArray } from 'lodash';
import styled from 'styled-components';
import Clarity from '@microsoft/clarity';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.min.css';
import { useSwipeable } from 'react-swipeable';
import { addDays, parseISO, differenceInDays, setYear, setMonth, setDate, getYear, startOfWeek } from 'date-fns';
import { dateFormat, useTranslations } from '@/app/i18n.js';
import { fr } from 'date-fns/locale/fr';
import { enUS } from 'date-fns/locale/en-US';
import GTMEventManager from '@/app/GTMEventManager.js';
import ActionButton from '@/app/Components/ActionButton.jsx';
import DoubleArrowsIcon from '@assets/icons/double-arrows.svg?react';
import Alert from '@/app/Components/Alert.jsx';
import AlternativeGames from './AlternativeGames.jsx';
import BookingTimeSlot from './BookingTimeSlot.jsx';
import { remainingCapacity } from './bookingSlotPresentation.js';
import { hasBookableArena } from './dateStepAvailability.js';

const AlertPopup = styled.div`
    right: 21rem;
    bottom: 1rem;

    @media screen and (min-width: 1920px) {
        right: 27rem;
    }

    @media screen and (max-width: 1024px) {
        right: 0;
        bottom: 1rem;
        width: 100%;
    }

    @media screen and (min-width: 1024px) and (max-width: 1280px) {
        right: 19rem;
    }
`;

export default function ({ settings, initialDate, team, openingHours, slots, onSelectDate, maxDays, locale = 'fr', selectedPointOfSale, game, games, onSelectAlternative, pricingExceptions = {} }) {
    // Register locale before any hooks (not a hook, but needs to be called consistently)
    const locales = { fr: fr, en: enUS };
    registerLocale(locale, locales[locale]);

    // All hooks must be called in the same order every render
    const { uniq_id: uniqId, session_id: sessionId } = usePage().props.auth;
    const __ = useTranslations();
    const [minDate, setMinDate] = useState(() => {
        const availableSlots = slots.filter(s => Object.keys(s.slots).length > 0);
        return availableSlots.length > 0 ? parseISO(availableSlots[0].date) : new Date();
    });
    const [selectedDate, setSelectedDate] = useState(initialDate?.date ? parseISO(initialDate.date) : null);
    const [selectedSlot, setSelectedSlot] = useState(initialDate?.date && initialDate?.time?.trim() ? `${initialDate.date} ${initialDate.time}:00` : null);
    const [alertContent, setAlertContent] = useState(null);
    const [alertId, setAlertId] = useState('default');
    const [unavailableSlot, setUnavailableSlot] = useState(false);
    const datepicker = useRef(null);

    // All hooks must be called before any function definitions
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => document.getElementById('datepicker-next-btn')?.click(),
        onSwipedRight: () => document.getElementById('datepicker-prev-btn')?.click(),
        swipeDuration: 500,
        preventScrollOnSwipe: true,
        trackMouse: true
    });

    const renderCustomHeader = ({
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }) => {
        return (
            <div className="flex flex-row items-center justify-between">
                <div
                    id="datepicker-prev-btn"
                    className={classNames({
                        'p-2.5 text-black dark:text-white transform rotate-180': true,
                        'cursor-pointer': !prevMonthButtonDisabled,
                        'opacity-50': prevMonthButtonDisabled,
                    })}
                    onClick={() => {
                        if (!prevMonthButtonDisabled) {
                            setSelectedDate(null);
                            setSelectedSlot(null);
                            onSelectDate({ date: null, time: ' ', slot: null });
                            decreaseMonth();
                        }
                    }}
                >
                    <DoubleArrowsIcon className="size-5" />
                </div>
                <h2 className="text-lg sm:text-xl lg:text-2xl 3xl:text-3xl font-bold capitalize">
                    {dateFormat(date, 'MMMM yyyy')}
                </h2>
                <div
                    id="datepicker-next-btn"
                    className={classNames({
                        'p-2.5 text-black dark:text-white': true,
                        'cursor-pointer': !nextMonthButtonDisabled,
                        'opacity-50': nextMonthButtonDisabled,
                    })}
                    onClick={() => {
                        if (!nextMonthButtonDisabled) {
                            setSelectedDate(null);
                            setSelectedSlot(null);
                            onSelectDate({ date: null, time: ' ', slot: null });
                            increaseMonth();
                        }
                    }}
                >
                    <DoubleArrowsIcon className="size-5" />
                </div>
            </div>
        );
    };

    const renderDayContents = (day, date) => {
        let specialDay = null;
        let specialDayTitle = null;
        let specialDayIcon = null;
        let hasException = false;
        const dateStr = dateFormat(date, 'yyyy-MM-dd');
        const dateSlots = slots.find(s => s.date === dateStr)?.slots ?? {};
        Object.keys(dateSlots).forEach(function (dateTime) {
            if (dateSlots[dateTime].find(s => s?.has_exception === true)) {
                hasException = true;
            }
        });

        const weekStartsOn = locale === 'fr' ? 1 : 0;
        const weekStart = startOfWeek(date, { weekStartsOn });
        const weekKey = dateFormat(weekStart, 'yyyy-MM-dd');
        const weekBanner = exceptionBannersByWeek[weekKey];
        const isFirstDayOfRangeInWeek = weekBanner && weekBanner.firstDate === dateStr;
        const lastDateInWeek = weekBanner ? parseISO(weekBanner.lastDate) : null;
        const lastDayIndexInWeek = lastDateInWeek ? differenceInDays(lastDateInWeek, weekStart) : 0;
        const dayIndexInWeek = differenceInDays(date, weekStart);
        const spanCount = isFirstDayOfRangeInWeek ? lastDayIndexInWeek - dayIndexInWeek + 1 : 0;

        return (
            <div className={`flex flex-col w-full py-2 lg:py-4 xl:py-8 3xl:py-10 user-select-none relative ${specialDay} ${isFirstDayOfRangeInWeek ? 'overflow-visible' : 'overflow-hidden'}`}>
                {isFirstDayOfRangeInWeek && spanCount > 0 && (
                    <span
                        className="exception-message-banner absolute left-0 top-0 z-10 truncate px-1 py-0.5 text-center text-xs font-bold leading-tight"
                        style={{
                            '--span-count': spanCount,
                            ...(weekBanner.message_background && { backgroundColor: weekBanner.message_background }),
                            ...(weekBanner.message_color && { color: weekBanner.message_color }),
                        }}
                    >
                        {weekBanner.message}
                    </span>
                )}
                <span className="text-xl sm:text-2xl lg:text-2xl 3xl:text-3xl font-semibold tracking-tighter user-select-none relative z-10">{day}</span>
            </div>
        );
    };

    const getSlots = () => {
        if (!selectedDate) return [];
        const formattedDate = dateFormat(selectedDate, 'yyyy-MM-dd');
        return slots.find(s => s.date === formattedDate)?.slots ?? [];
    }

    const bookable = (slots) => hasBookableArena(slots, team);

    const attributedSlot = (datetime) => {
        if (!selectedDate) return null;
        const formattedDate = dateFormat(selectedDate, 'yyyy-MM-dd');
        const selectedSlot = slots.find(s => s.date === formattedDate)?.slots[datetime];

        if (!selectedSlot) return selectedSlot;

        let attributedArena = null;
        const possibleArenas = selectedSlot
        .filter((s) => s.bookable && s.arena_public !== false)
        .filter((s) => s.max_seats - s.booked_seats >= team)
        .sort((a, b) => b.booked_seats - a.booked_seats);

        attributedArena = Array.from(possibleArenas).shift();

        if (!attributedArena) return null;

        // More than one arena entirely free
        if (possibleArenas.length > 1 && possibleArenas.reduce((acc, v) => acc + v.booked_seats, 0) === 0) {
            // Order by max seats; smallest first
            if ([...new Set(possibleArenas.map((a) => a.max_seats))].length > 1) {
                attributedArena = possibleArenas.sort((a, b) => a.max_seats - b.max_seats).shift();
                // If size equals order by duration, smallest first
            } else {
                attributedArena = possibleArenas.sort((a, b) => a.duration - b.duration).shift();
            }
        }

        // Use pricing data from slot (includes prices for all persons counts)
        // slot.pricing[team] contains the resolved price for the selected team size
        if (attributedArena && attributedArena.pricing && typeof attributedArena.pricing === 'object') {
            const resolvedPrice = attributedArena.pricing[team] ?? attributedArena.pricing[1] ?? attributedArena.price;
            attributedArena = { ...attributedArena, price: resolvedPrice };
        }

        return attributedArena;
    };

    const getPricingExceptionForDate = (dateStr) => {
        for (const exception of Object.keys(pricingExceptions)) {
            if (exception.indexOf(' to ') !== -1) {
                const dates = exception.split(' to ');
                const yearRecurrent = dates[0].split('-').length === 2;
                const currentYear = getYear(new Date());
                const startParts = dates[0].split('-');
                const endParts = dates[1].split('-');

                const rangeStart = yearRecurrent
                    ? setDate(setMonth(setYear(new Date(), currentYear), parseInt(startParts[0]) - 1), parseInt(startParts[1]))
                    : setDate(setMonth(setYear(new Date(), parseInt(startParts[0])), parseInt(startParts[1]) - 1), parseInt(startParts[2]));

                const rangeEnd = yearRecurrent
                    ? setDate(setMonth(setYear(new Date(), currentYear), parseInt(endParts[0]) - 1), parseInt(endParts[1]))
                    : setDate(setMonth(setYear(new Date(), parseInt(endParts[0])), parseInt(endParts[1]) - 1), parseInt(endParts[2]));

                const daysDiff = differenceInDays(rangeEnd, rangeStart);
                for (let i = 0; i <= daysDiff; i++) {
                    const d = addDays(rangeStart, i);
                    if (dateFormat(d, 'yyyy-MM-dd') === dateStr) {
                        return pricingExceptions[exception];
                    }
                }
            } else {
                const parts = exception.split('-');
                const yearRecurrent = parts.length === 2;
                const currentYear = getYear(new Date());
                const date = yearRecurrent
                    ? setDate(setMonth(setYear(new Date(), currentYear), parseInt(parts[0]) - 1), parseInt(parts[1]))
                    : setDate(setMonth(setYear(new Date(), parseInt(parts[0])), parseInt(parts[1]) - 1), parseInt(parts[2]));
                if (dateFormat(date, 'yyyy-MM-dd') === dateStr) {
                    return pricingExceptions[exception];
                }
            }
        }
        return null;
    };

    useEffect(() => {
        if (selectedDate) {
            if (selectedSlot && (!getSlots()[selectedSlot] || !bookable(getSlots()[selectedSlot]))) {
                setSelectedSlot(null);
                onSelectDate({
                    date: dateFormat(selectedDate, 'yyyy-MM-dd'),
                    time: ' ',
                    slot: null,
                });
            }

            const dateStr = dateFormat(selectedDate, 'yyyy-MM-dd');
            const exception = getPricingExceptionForDate(dateStr);
            const alertContentFromException = exception?.alert_content;

            const hasExceptionAlertContent = alertContentFromException
                && typeof alertContentFromException === 'object'
                && Object.values(alertContentFromException).some((v) => v && String(v).trim());

            setAlertId('default');
            if (hasExceptionAlertContent) {
                const content = {
                    fr: alertContentFromException.fr ?? '',
                    en: alertContentFromException.en ?? '',
                };
                setAlertContent({
                    desktop: content,
                    mobile: content,
                });
            } else {
                setAlertContent({
                    desktop: {
                        fr: settings?.slot_alert_content?.['fr'],
                        en: settings?.slot_alert_content?.['en'],
                    },
                    mobile: {
                        fr: settings?.slot_alert_mobile_content?.['fr'],
                        en: settings?.slot_alert_mobile_content?.['en'],
                    },
                });
            }
        }
    }, [team, selectedDate, slots, pricingExceptions, settings]);

    const excludedDates = useMemo(() => {
        let excluded = [];
        const allDates = slots.map(s => s.date);

        // exclude dates with no opening hours
        for (let i = 0; i <= parseInt(maxDays); i++) {
            const date = addDays(new Date(), i);
            let dateIndex = allDates.findIndex(d => d === dateFormat(date, 'yyyy-MM-dd'));
            if (dateIndex === -1) {
                excluded.push(date);
            }
        }

        // exclude dates with no slots
        slots.filter(s => Object.keys(s.slots).length === 0).forEach((s) => {
            excluded.push(parseISO(s.date));
        });

        return excluded;
    }, [openingHours, slots, maxDays]);

    const exceptionBannersByWeek = useMemo(() => {
        const weekToBanner = {};
        const weekStartsOn = locale === 'fr' ? 1 : 0;

        Object.keys(pricingExceptions).forEach((exception) => {
            if (exception.indexOf(' to ') === -1) return;
            const message = pricingExceptions[exception]?.message;
            if (!message || typeof message !== 'string' || message.trim() === '') return;

            const dates = exception.split(' to ');
            const yearRecurrent = dates[0].split('-').length === 2;
            const currentYear = getYear(new Date());
            const startParts = dates[0].split('-');
            const endParts = dates[1].split('-');

            const rangeStart = yearRecurrent
                ? setDate(setMonth(setYear(new Date(), currentYear), parseInt(startParts[0]) - 1), parseInt(startParts[1]))
                : setDate(setMonth(setYear(new Date(), parseInt(startParts[0])), parseInt(startParts[1]) - 1), parseInt(startParts[2]));

            const rangeEnd = yearRecurrent
                ? setDate(setMonth(setYear(new Date(), currentYear), parseInt(endParts[0]) - 1), parseInt(endParts[1]))
                : setDate(setMonth(setYear(new Date(), parseInt(endParts[0])), parseInt(endParts[1]) - 1), parseInt(endParts[2]));

            const daysDiff = differenceInDays(rangeEnd, rangeStart);
            const messageBackground = pricingExceptions[exception]?.message_background ?? null;
            const messageColor = pricingExceptions[exception]?.message_color ?? null;
            for (let i = 0; i <= daysDiff; i++) {
                const d = addDays(rangeStart, i);
                const weekStart = startOfWeek(d, { weekStartsOn });
                const weekKey = dateFormat(weekStart, 'yyyy-MM-dd');
                const dateStr = dateFormat(d, 'yyyy-MM-dd');
                if (!weekToBanner[weekKey]) {
                    weekToBanner[weekKey] = { firstDate: dateStr, lastDate: dateStr, message, message_background: messageBackground, message_color: messageColor };
                } else {
                    weekToBanner[weekKey].lastDate = dateStr;
                }
            }
        });
        return weekToBanner;
    }, [pricingExceptions, locale]);

    const holidays = useMemo(() => {
        let excluded = [];

        Object.keys(openingHours.exceptions).forEach((exception) => {
            if (exception.indexOf(' to ') !== -1) {
                // is days range
                const dates = exception.split(' to ');
                const yearRecurrent = dates[0].split('-').length === 2;

                const currentYear = getYear(new Date());
                const startParts = dates[0].split('-');
                const endParts = dates[1].split('-');

                const start = yearRecurrent
                    ? setDate(
                        setMonth(
                            setYear(new Date(), currentYear),
                            parseInt(startParts[0]) - 1
                        ),
                        parseInt(startParts[1])
                    )
                    : setDate(
                        setMonth(
                            setYear(new Date(), parseInt(startParts[0])),
                            parseInt(startParts[1]) - 1
                        ),
                        parseInt(startParts[2])
                    );

                const end = yearRecurrent
                    ? setDate(
                        setMonth(
                            setYear(new Date(), currentYear),
                            parseInt(endParts[0]) - 1
                        ),
                        parseInt(endParts[1])
                    )
                    : setDate(
                        setMonth(
                            setYear(new Date(), parseInt(endParts[0])),
                            parseInt(endParts[1]) - 1
                        ),
                        parseInt(endParts[2])
                    );

                const daysDiff = differenceInDays(end, start);

                for (let i = 0; i <= daysDiff; i++) {
                    excluded.push({
                        date: dateFormat(addDays(start, i), 'yyyy-MM-dd'),
                        holidayName: openingHours.exceptions[exception].data
                    });
                }
            } else if (!isArray(openingHours.exceptions[exception]) && !(Object.hasOwn(openingHours.exceptions[exception], 'hours') && openingHours.exceptions[exception].hours.length > 0)) {
                const yearRecurrent = exception.split('-').length === 2;
                const parts = exception.split('-');
                const currentYear = getYear(new Date());

                const date = yearRecurrent
                    ? setDate(
                        setMonth(
                            setYear(new Date(), currentYear),
                            parseInt(parts[0]) - 1
                        ),
                        parseInt(parts[1])
                    )
                    : setDate(
                        setMonth(
                            setYear(new Date(), parseInt(parts[0])),
                            parseInt(parts[1]) - 1
                        ),
                        parseInt(parts[2])
                    );

                excluded.push({
                    date: dateFormat(date, 'yyyy-MM-dd'),
                    holidayName: openingHours.exceptions[exception].data
                });
            }
        });

        return excluded;
    }, [openingHours]);

    const pricings = useMemo(() => {
        let data = []
        slots.forEach((slot) => {
            Object.entries(slot.slots).forEach(([d, s]) => {
                if (s[0]) {
                    // Use pricing data from slot (for current team size)
                    const slotData = s[0];
                    let price = slotData.price; // Base price
                    if (slotData.pricing && typeof slotData.pricing === 'object') {
                        // Use price for current team size, or fallback to base price
                        price = slotData.pricing[team] ?? slotData.pricing[1] ?? slotData.price;
                    }
                    if (data.indexOf(price) === -1) data.push(price);
                }
            });
        });
        data = data.sort((a, b) => a - b).reverse();
        return data;
    }, [slots, team]);

    useEffect(() => {
        GTMEventManager.pageView('step_schedule');
        console.log(`[Clarity] session: ${uniqId} / ${sessionId} - step_schedule`);
        Clarity.identify(uniqId, sessionId, 'step_schedule');
        const query = new URLSearchParams(window.location.search);
        if (query.has('unavailable')) {
            setUnavailableSlot(true);
        }
    }, []);

    useEffect(() => {
        if (!selectedDate && datepicker.current) {
            datepicker.current.setPreSelection(null);
        }
    }, [selectedDate]);

    return (
        <section className="booking-date-container user-select-none px-4 sm:px-5 md:px-6 dark:px-0 mb-12 dark:mb-0 lg:mr-6 dark:mr-0">
            <div {...swipeHandlers}>
                <DatePicker
                    inline
                    locale={locale}
                    selected={selectedDate}
                    ref={datepicker}
                    onChange={(date) => {
                        setSelectedDate(date);
                        setSelectedSlot(null);
                        onSelectDate({
                            date: dateFormat(date, 'yyyy-MM-dd'),
                            time: ' ',
                            slot: null,
                        });
                        setTimeout(() => {
                            const element = document.getElementById('time-picker');
                            const offset = 50; // Adjust this value to your desired offset
                            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                            const offsetPosition = elementPosition - offset;

                            window.scrollTo({
                                top: offsetPosition,
                                behavior: 'smooth',
                            });
                        }, 200);
                    }}
                    renderCustomHeader={renderCustomHeader}
                    renderDayContents={renderDayContents}
                    minDate={minDate}
                    maxDate={slots.length ? parseISO(slots[slots.length - 1].date) : addDays(new Date(), Number(maxDays) || 0)}
                    excludeDates={excludedDates.concat(holidays.map(h => parseISO(h.date)))}
                    holidays={holidays}
                />
            </div>

            {selectedDate && (
                <div className="flex flex-col gap-10 dark:px-4 pt-6 pb-3 lg:pt-8 dark:sm:px-6 dark:3xl:px-8" id="time-picker">
                    <h4 id="booking-times-title" tabIndex={-1} className="text-black dark:text-white text-md sm:text-lg 3xl:text-xl font-semibold">
                        {__('book.index.steps.date.slot_title')}
                    </h4>
                </div>
            )}
            {selectedDate && alertContent && (alertContent.desktop?.[locale] || alertContent.mobile?.[locale]) && (
                <div className="mb-5">
                    <Alert secondary id={alertId}>
                        <span className="text-base 3xl:text-lg hidden lg:block truncate" dangerouslySetInnerHTML={{ __html: alertContent.desktop?.[locale] ?? '' }} />
                        <span className="text-base lg:hidden truncate" dangerouslySetInnerHTML={{ __html: alertContent.mobile?.[locale] ?? '' }} />
                    </Alert>
                </div>
            )}
            <div className="grid grid-slots-bordered grid-cols-3 md:grid-cols-4 xl:grid-cols-7">
                {selectedDate && Object.entries(getSlots()).map(([key, candidates]) => {
                    const sharedChoice = initialDate?.shared_arena_id && key === `${initialDate.date} ${initialDate.time}:00` ? initialDate : null;
                    const slot = sharedChoice?.slot ?? attributedSlot(key);
                    const seats = remainingCapacity(candidates, slot);
                    return <BookingTimeSlot key={key} time={dateFormat(parseISO(key), 'HH:mm')}
                        slot={slot} available={bookable(candidates)} selected={selectedSlot === key}
                        prices={pricings} remaining={seats.remaining} capacity={seats.capacity}
                        onSelect={() => {
                            setSelectedSlot(key);
                            onSelectDate({
                                ...(sharedChoice ? { shared_arena_id: sharedChoice.shared_arena_id } : {}),
                                date: dateFormat(parseISO(key), 'yyyy-MM-dd'),
                                time: dateFormat(parseISO(key), 'HH:mm'),
                                slot,
                            });
                        }} />;
                })}
            </div>

            {selectedDate && game && team && <AlternativeGames
                key={`${selectedPointOfSale.id}:${game.id}:${team}:${dateFormat(selectedDate, 'yyyy-MM-dd')}`}
                pointOfSale={selectedPointOfSale} game={game} games={games} team={team} locale={locale}
                date={dateFormat(selectedDate, 'yyyy-MM-dd')} onSelect={onSelectAlternative}
            />}

            <Transition appear show={unavailableSlot} as={Fragment}>
                <AlertPopup className="fixed z-[100]" onClose={() => {}}>
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <div className="w-full lg:max-w-md transform overflow-hidden rounded-2xl bg-light-gray text-white p-6 text-left align-middle shadow-2xl border border-light-gray transition-all">
                                <h3 className="text-lg font-medium leading-6 text-gray-800">
                                    {__('book.booking_unavailable.tooltip.title')}
                                </h3>
                                <div className="mt-2 flex flex-row items-center gap-2">
                                    <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: __('book.booking_unavailable.tooltip.content', { time: initialDate?.time ?? '' })}} />
                                </div>

                                <div className="mt-4">
                                    <ActionButton onClick={() => { setUnavailableSlot(false) }} style={{'background': '#363636'}}>
                                        {__('book.booking_unavailable.tooltip.button_cta')}
                                    </ActionButton>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </AlertPopup>
            </Transition>
        </section>
    );
}
